import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Container, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { createCalendarEvent, getStandardEvents } from '../services/api';
import { toast } from 'react-toastify';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import DatePicker from 'react-datepicker';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-datepicker/dist/react-datepicker.css';

// Google Calendar Color IDs
const googleCalendarColors = [
    { id: '1', name: 'Lavanda', hex: '#7986cb' },
    { id: '2', name: 'Salvia', hex: '#33b679' },
    { id: '3', name: 'Uva', hex: '#8e24aa' },
    { id: '4', name: 'Fenicottero', hex: '#e67c73' },
    { id: '5', name: 'Banana', hex: '#f6c026' },
    { id: '6', name: 'Mandarino', hex: '#f5511d' },
    { id: '7', name: 'Pavone', hex: '#039be5' },
    { id: '8', name: 'Grafite', hex: '#616161' },
    { id: '9', name: 'Mirtillo', hex: '#3f51b5' },
    { id: '10', name: 'Basilico', hex: '#0b8043' },
    { id: '11', name: 'Pomodoro', hex: '#d60000' },
];

const NuovoEventoCalendario = () => {
    const navigate = useNavigate();
    
    // State
    const [summary, setSummary] = useState([]);
    const [allDay, setAllDay] = useState(false);
    const [start, setStart] = useState(new Date());
    const [end, setEnd] = useState(new Date(new Date().getTime() + 60 * 60 * 1000)); // +1 hour
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [transparency, setTransparency] = useState('opaque'); // 'opaque' (Busy) or 'transparent' (Free)
    const [colorId, setColorId] = useState(googleCalendarColors[6].id); // Default to Pavone
    const [reminders, setReminders] = useState([{ minutes: 10, method: 'popup' }]);
    
    const [typeaheadOptions, setTypeaheadOptions] = useState([]);
    const [isTypeaheadLoading, setIsTypeaheadLoading] = useState(false);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleTypeaheadSearch = (query) => {
        setIsTypeaheadLoading(true);
        getStandardEvents()
            .then(resp => {
                const options = resp.data.map(event => ({ id: event.id, title: event.title }));
                setTypeaheadOptions(options);
                setIsTypeaheadLoading(false);
            })
            .catch(err => {
                console.error("Failed to fetch standard events", err);
                setIsTypeaheadLoading(false);
            });
    };

    const handleAddReminder = () => {
        setReminders([...reminders, { minutes: 30, method: 'popup' }]);
    };

    const handleRemoveReminder = (index) => {
        const newReminders = reminders.filter((_, i) => i !== index);
        setReminders(newReminders);
    };

    const handleReminderChange = (index, value) => {
        const newReminders = reminders.map((reminder, i) => 
            i === index ? { ...reminder, minutes: parseInt(value, 10) || 0 } : reminder
        );
        setReminders(newReminders);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const finalSummary = summary.length > 0 ? (summary[0].title || '') : '';

        if (!finalSummary.trim()) {
            setError('Il titolo è obbligatorio.');
            toast.warn('Il titolo è obbligatorio.');
            return;
        }

        if (!allDay && end <= start) {
            setError('La data/ora di fine deve essere successiva a quella di inizio.');
            toast.warn('La data/ora di fine deve essere successiva a quella di inizio.');
            return;
        }

        const eventData = {
            summary: finalSummary,
            location,
            description,
            start: start,
            end: end,
            all_day: allDay,
            reminders,
            color_id: colorId,
            transparency,
        };

        setLoading(true);
        try {
            await createCalendarEvent(eventData);
            toast.success('Evento creato con successo su Google Calendar!');
            navigate('/calendario');
        } catch (err) {
            console.error("Errore durante la creazione dell'evento:", err);
            setError('Si è verificato un errore durante la creazione dell\'evento.');
            toast.error('Errore durante la creazione dell\'evento.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container>
            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <Card className="shadow-sm">
                        <Card.Header as="h5">Crea Nuovo Evento</Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3" controlId="summary">
                                    <Form.Label>Titolo Evento</Form.Label>
                                    <AsyncTypeahead
                                        id="event-title-typeahead"
                                        isLoading={isTypeaheadLoading}
                                        labelKey="title"
                                        onSearch={handleTypeaheadSearch}
                                        options={typeaheadOptions}
                                        placeholder="Scegli o digita un titolo..."
                                        onChange={setSummary}
                                        allowNew
                                        selected={summary}
                                        renderMenuItemChildren={(option) => (
                                            <span>{option.title}</span>
                                        )}
                                    />
                                </Form.Group>

                                <Row className="align-items-center">
                                    <Col md={5}>
                                        <Form.Group className="mb-3" controlId="start">
                                            <Form.Label>Inizio</Form.Label>
                                            <DatePicker
                                                selected={start}
                                                onChange={(date) => setStart(date)}
                                                showTimeSelect={!allDay}
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat={allDay ? "d MMMM yyyy" : "d MMMM yyyy, HH:mm"}
                                                className="form-control"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Group className="mb-3" controlId="end">
                                            <Form.Label>Fine</Form.Label>
                                            <DatePicker
                                                selected={end}
                                                onChange={(date) => setEnd(date)}
                                                showTimeSelect={!allDay}
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat={allDay ? "d MMMM yyyy" : "d MMMM yyyy, HH:mm"}
                                                className="form-control"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3" controlId="allDay">
                                            <Form.Check 
                                                type="checkbox"
                                                label="Tutto il giorno"
                                                checked={allDay}
                                                onChange={(e) => setAllDay(e.target.checked)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3" controlId="location">
                                    <Form.Label>Località</Form.Label>
                                    <Form.Control type="text" name="location" value={location} onChange={(e) => setLocation(e.target.value)} />
                                </Form.Group>
                                
                                <Form.Group className="mb-3" controlId="description">
                                    <Form.Label>Descrizione</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="description" value={description} onChange={(e) => setDescription(e.target.value)} />
                                </Form.Group>

                                <Form.Group className="mb-3" controlId="reminders">
                                    <Form.Label>Promemoria</Form.Label>
                                    {reminders.map((reminder, index) => (
                                        <InputGroup className="mb-2" key={index}>
                                            <Form.Control
                                                type="number"
                                                value={reminder.minutes}
                                                onChange={(e) => handleReminderChange(index, e.target.value)}
                                            />
                                            <InputGroup.Text>minuti prima</InputGroup.Text>
                                            <Button variant="outline-danger" onClick={() => handleRemoveReminder(index)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        </InputGroup>
                                    ))}
                                    <Button variant="outline-secondary" size="sm" onClick={handleAddReminder}>
                                        <FontAwesomeIcon icon={faPlus} /> Aggiungi promemoria
                                    </Button>
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="colorId">
                                            <Form.Label>Colore Evento</Form.Label>
                                            <div>
                                                {googleCalendarColors.map(color => (
                                                    <Button
                                                        key={color.id}
                                                        variant="light"
                                                        onClick={() => setColorId(color.id)}
                                                        className={`p-3 me-1 border ${colorId === color.id ? 'border-primary border-3' : ''}`}
                                                        style={{ backgroundColor: color.hex, width: '40px', height: '40px' }}
                                                        title={color.name}
                                                    />
                                                ))}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3" controlId="transparency">
                                            <Form.Label>Mostrami come</Form.Label>
                                            <Form.Select value={transparency} onChange={(e) => setTransparency(e.target.value)}>
                                                <option value="opaque">Occupato</option>
                                                <option value="transparent">Libero</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end mt-4">
                                    <Button variant="secondary" onClick={() => navigate('/calendario')} className="me-2">
                                        Annulla
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={loading}>
                                        {loading ? 'Creazione...' : 'Crea Evento'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default NuovoEventoCalendario;